import React, { useState } from 'react';
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, Sparkles, Brain, Sofa, Heart } from "lucide-react";

interface Mode {
  id: string;
  icon: React.ReactNode;
  title: string;
  oneLiner: string;
  learnMore: string;
}

interface TherappiModeChooserProps {
  onSelectMode: (mode: string) => void;
  hasTimeline?: boolean;
}

export const TherappiModeChooser: React.FC<TherappiModeChooserProps> = ({ 
  onSelectMode,
  hasTimeline = false 
}) => {
  const [selectedMode, setSelectedMode] = useState<string | null>(null);
  const [expandedMode, setExpandedMode] = useState<string | null>(null);

  const modes: Mode[] = [
    {
      id: 'general',
      icon: <Heart className="w-6 h-6 text-primary" />,
      title: 'General Mode',
      oneLiner: 'A compassionate, person-centred space for reflection and everyday support. (3 credits per exchange)',
      learnMore: 'Gentle exploration to help you name what you feel, unpack what matters, and choose one kind action. Designed for grounding and clarity on ordinary days. Each back-and-forth conversation (your message + AI response) uses 3 credits.'
    },
    {
      id: 'cbt',
      icon: <Brain className="w-6 h-6 text-primary" />,
      title: 'CBT Mode',
      oneLiner: 'A structured, evidence-based pathway to rewire unhelpful patterns. (3 credits per exchange)',
      learnMore: 'Identify thought-feeling-behaviour cycles, test beliefs, run behavioural experiments, and practise new skills. Clear, step-by-step change. Each back-and-forth conversation (your message + AI response) uses 3 credits.'
    },
    {
      id: 'psychotherapy',
      icon: <Sofa className="w-6 h-6 text-primary" />,
      title: 'Psychotherapy Mode',
      oneLiner: 'A deeper dive into unconscious patterns and early roots. (3 credits per exchange)',
      learnMore: 'Explore repeating dynamics, attachment themes, and inner conflicts with empathy. Slow, reflective work that creates lasting integration. Each back-and-forth conversation (your message + AI response) uses 3 credits.'
    },
    {
      id: 'qsp',
      icon: <Sparkles className="w-6 h-6 text-primary" />,
      title: 'QuantumSelf Psychology™ Mode (QSP)',
      oneLiner: 'An advanced, multidimensional approach where science meets soul. (3 credits per exchange)',
      learnMore: 'Bridges psychology, quantum science, and consciousness studies. Understand how energy, emotion, and intention interact across time. Reconnect, Release, and Reprogramme with precision-so your future isn\'t a repeat of your past. Each back-and-forth conversation (your message + AI response) uses 3 credits.'
    }
  ];

  const handleSelectMode = (modeId: string) => {
    setSelectedMode(modeId);
  };

  const handleStartSession = () => {
    if (selectedMode) {
      onSelectMode(selectedMode);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-light text-foreground">
          Choose how you'd like Therappi to guide you today
        </h2>
        <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
          {hasTimeline 
            ? "When available, Therappi integrates your timeline and any conditions you've shared."
            : "All modes adapt intelligently to support you, even without timeline data."}
        </p>
        <p className="text-xs text-muted-foreground/80 max-w-xl mx-auto pt-2">
          💡 Credit usage: Each exchange = your message + AI response. Multiple messages in one conversation count as separate exchanges.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {modes.map((mode) => (
          <Card 
            key={mode.id}
            className={`p-6 cursor-pointer transition-all ${
              selectedMode === mode.id 
                ? 'ring-2 ring-primary bg-primary/5' 
                : 'hover:border-primary/50'
            }`}
            onClick={() => handleSelectMode(mode.id)}
          >
            <div className="flex items-start gap-4">
              <div className="shrink-0 mt-1">{mode.icon}</div>
              <div className="flex-1 space-y-2">
                <h3 className="font-semibold text-foreground">{mode.title}</h3>
                <p className="text-sm text-muted-foreground">{mode.oneLiner}</p>
                
                <Collapsible
                  open={expandedMode === mode.id}
                  onOpenChange={(open) => setExpandedMode(open ? mode.id : null)}
                >
                  <CollapsibleTrigger 
                    className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Learn more
                    <ChevronDown className={`w-4 h-4 transition-transform ${
                      expandedMode === mode.id ? 'rotate-180' : ''
                    }`} />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-2">
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {mode.learnMore}
                    </p>
                  </CollapsibleContent>
                </Collapsible>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {!hasTimeline && (
        <Card className="p-4 bg-muted/30 border-primary/20">
          <p className="text-sm text-muted-foreground text-center">
            <span className="font-medium text-foreground">Note:</span> Timeline/conditions unavailable here; Therappi still adapts intelligently by building a mini-timeline with you.
          </p>
        </Card>
      )}

      <div className="flex flex-col items-center gap-3">
        <Button 
          size="lg"
          onClick={handleStartSession}
          disabled={!selectedMode}
          className="w-full max-w-md"
        >
          Start session
        </Button>
        <Button 
          variant="link"
          size="sm"
          className="text-muted-foreground"
          onClick={() => window.open('https://docs.lovable.dev', '_blank')}
        >
          Learn about modes
        </Button>
      </div>
    </div>
  );
};
